import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import type { EssayAttachmentReadback } from "@/types/agentic-chat";
import { AlertTriangle, CheckCircle2, ScanText } from "lucide-react";
import { useEffect, useState } from "react";

type EssayReadbackCardProps = {
  readback: EssayAttachmentReadback;
  onConfirm: (text: string) => void;
  onRetake?: () => void;
};

export function EssayReadbackCard({ readback, onConfirm, onRetake }: EssayReadbackCardProps) {
  const [draft, setDraft] = useState(readback.extractedText);
  const isLowConfidence = readback.confidence === "low";

  useEffect(() => {
    setDraft(readback.extractedText);
  }, [readback.extractedText]);

  return (
    <div className="space-y-2 rounded-[1rem] border border-blue-100/85 bg-[linear-gradient(180deg,rgba(239,246,255,0.92),rgba(219,234,254,0.76))] p-3 shadow-[0_10px_24px_rgba(59,130,246,0.08)]">
      <div className="flex items-center gap-2">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/80 text-blue-600">
          {isLowConfidence ? <AlertTriangle className="h-3.5 w-3.5" /> : <ScanText className="h-3.5 w-3.5" />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold text-slate-900">I read your answer as:</p>
          <p className="text-[10px] text-slate-600">Confirm before I evaluate it.</p>
        </div>
        <span
          className={cn(
            "rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.12em]",
            isLowConfidence ? "bg-amber-100/85 text-amber-800" : "bg-white/80 text-blue-700",
          )}
        >
          {readback.confidence}
        </span>
      </div>
      <Textarea
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        className="min-h-[92px] rounded-[0.95rem] border-white/75 bg-white/88 text-[12px] leading-5"
      />
      <p className="text-[10px] leading-4 text-slate-600">{readback.summary}</p>
      {isLowConfidence ? (
        <p className="text-[10px] leading-4 text-amber-800">
          This looks low confidence. Edit the text or retake the photo if anything is off.
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          className="h-8 rounded-full px-3 text-[10px]"
          disabled={!draft.trim()}
          onClick={() => onConfirm(draft.trim())}
        >
          <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
          Looks right
        </Button>
        {onRetake ? (
          <Button type="button" variant="outline" className="h-8 rounded-full px-3 text-[10px]" onClick={onRetake}>
            Retake photo
          </Button>
        ) : null}
      </div>
    </div>
  );
}
